/**
 * API Client
 *
 * Handles communication with the backend API.
 */

import { EventsResponse, SummaryResponse, WindData, WindForecastResponse, AircraftResponse, AircraftCacheResponse, SocialIntelResponse } from '../types/events';

/**
 * Determine the API base URL dynamically.
 *
 * Detection logic:
 * 1. If VITE_API_URL env var is set, use that
 * 2. If accessing via localhost/127.0.0.1, use '/api' (Vite proxy)
 * 3. If accessing via production domain (cloudfront.net, etc.), throw error - VITE_API_URL must be set
 * 4. If accessing via network IP (e.g., mobile on LAN), connect directly to backend on port 8000
 */
export function getApiBaseUrl(): string {
  // If explicitly set via environment variable, use that (REQUIRED for production)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  const hostname = window.location.hostname;

  // If accessing via localhost, use the proxy (works in dev)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '/api';
  }

  // If accessing via production domain (CloudFront, custom domain, etc.)
  // VITE_API_URL must be set during build - this is a configuration error
  if (hostname.includes('cloudfront.net') || hostname.includes('amazonaws.com') || !hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    console.error('[API Client] Production deployment detected but VITE_API_URL not set!');
    console.error('[API Client] Please rebuild with: VITE_API_URL=https://your-api-gateway-url npm run build');
    // Return a placeholder that will cause clear errors
    return '/api-not-configured';
  }

  // If accessing via network IP (e.g., from mobile device on same network),
  // connect directly to backend on port 8000
  // This works for dev mode when testing from mobile
  return `http://${hostname}:8000`;
}

const API_BASE_URL = getApiBaseUrl();

// Debug logging for development only
if (import.meta.env.DEV) {
  console.log('[API Client] Base URL:', API_BASE_URL);
  console.log('[API Client] Hostname:', window.location.hostname);
}

/**
 * Custom error class for API errors.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Format API error detail for display.
 * Handles FastAPI validation errors which return an array of objects.
 */
function formatErrorDetail(detail: unknown): string {
  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail)) {
    // FastAPI validation errors: [{ loc: [...], msg: "...", type: "..." }, ...]
    const messages = detail.map((err) => {
      if (typeof err === 'object' && err !== null && 'msg' in err) {
        const loc = Array.isArray(err.loc) ? err.loc.slice(1).join('.') : '';
        return loc ? `${loc}: ${err.msg}` : String(err.msg);
      }
      return String(err);
    });
    return messages.join(', ');
  }

  if (typeof detail === 'object' && detail !== null) {
    // Try to extract message from object
    if ('message' in detail) return String((detail as { message: unknown }).message);
    if ('msg' in detail) return String((detail as { msg: unknown }).msg);
    return JSON.stringify(detail);
  }

  return String(detail);
}

/**
 * Fetch recent events near a location.
 */
export async function fetchRecentEvents(params: {
  lat: number;
  lon: number;
  radiusKm: number;
  minutesBack: number;
}): Promise<EventsResponse> {
  const queryParams = new URLSearchParams({
    lat: params.lat.toString(),
    lon: params.lon.toString(),
    radiusKm: params.radiusKm.toString(),
    minutesBack: params.minutesBack.toString(),
  });

  const response = await fetch(`${API_BASE_URL}/events/recent?${queryParams}`);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.error || 'Failed to fetch events',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Fetch event summary for a location.
 */
export async function fetchEventSummary(params: {
  lat: number;
  lon: number;
  radiusKm: number;
  minutesBack: number;
}): Promise<SummaryResponse> {
  const queryParams = new URLSearchParams({
    lat: params.lat.toString(),
    lon: params.lon.toString(),
    radiusKm: params.radiusKm.toString(),
    minutesBack: params.minutesBack.toString(),
  });

  const response = await fetch(`${API_BASE_URL}/events/summary?${queryParams}`);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.error || 'Failed to fetch summary',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Check API health.
 */
export async function checkHealth(): Promise<{
  status: string;
  lastIngestionRunAt: string | null;
  region: string;
}> {
  const response = await fetch(`${API_BASE_URL}/health`);

  if (!response.ok) {
    throw new ApiError('Health check failed', response.status);
  }

  return response.json();
}

/**
 * Fetch wind data for a specific location.
 */
export async function fetchWindData(params: {
  lat: number;
  lon: number;
}): Promise<WindData> {
  const queryParams = new URLSearchParams({
    lat: params.lat.toString(),
    lon: params.lon.toString(),
  });

  const response = await fetch(`${API_BASE_URL}/weather/wind?${queryParams}`);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch wind data',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Fetch wind forecast for a specific location.
 */
export async function fetchWindForecast(params: {
  lat: number;
  lon: number;
  hours?: number;
}): Promise<WindForecastResponse> {
  const queryParams = new URLSearchParams({
    lat: params.lat.toString(),
    lon: params.lon.toString(),
  });

  if (params.hours) {
    queryParams.set('hours', params.hours.toString());
  }

  const response = await fetch(`${API_BASE_URL}/weather/wind-forecast?${queryParams}`);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch wind forecast',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * City temperature data structure.
 */
export interface CityTemperature {
  name: string;
  lat: number;
  lon: number;
  temperature: number | null;
  apparentTemperature: number | null;
  weatherCode: number | null;
}

export interface CityTemperaturesResponse {
  cities: CityTemperature[];
  source: string;
  timestamp: string;
}

/**
 * Fetch current temperatures for major Australian cities.
 */
export async function fetchCityTemperatures(): Promise<CityTemperaturesResponse> {
  const response = await fetch(`${API_BASE_URL}/weather/city-temperatures`);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch city temperatures',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Fetch live aircraft within a bounding box from OpenSky Network.
 */
export async function fetchAircraft(params: {
  north: number;
  south: number;
  east: number;
  west: number;
  helicoptersOnly?: boolean;
}): Promise<AircraftResponse> {
  const queryParams = new URLSearchParams({
    north: params.north.toString(),
    south: params.south.toString(),
    east: params.east.toString(),
    west: params.west.toString(),
  });

  if (params.helicoptersOnly) {
    queryParams.set('helicopters_only', 'true');
  }

  const response = await fetch(`${API_BASE_URL}/aircraft/live?${queryParams}`);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch aircraft data',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Aircraft cache URL — served from CloudFront, updated every 5 seconds by Lambda poller.
 * Falls back to null if not configured (feature disabled).
 */
const AIRCRAFT_CACHE_URL: string | null = import.meta.env.VITE_AIRCRAFT_CACHE_URL || null;

/**
 * Fetch cached aircraft data from CloudFront.
 *
 * This is the primary aircraft data source for the premium aircraft tracking feature.
 * Data is pre-filtered (low-altitude, emergency, helicopters) and covers all of Australia.
 * No authentication required — served via public CloudFront edge cache.
 *
 * Returns null if the aircraft cache is not configured.
 */
export async function fetchAircraftCached(): Promise<AircraftCacheResponse | null> {
  if (!AIRCRAFT_CACHE_URL) {
    return null;
  }

  const response = await fetch(AIRCRAFT_CACHE_URL, {
    // Always get fresh from CloudFront edge (respects s-maxage)
    cache: 'no-cache',
  });

  if (!response.ok) {
    throw new ApiError(
      'Failed to fetch aircraft data',
      response.status
    );
  }

  return response.json();
}

/**
 * Check if the aircraft cache is configured and available.
 */
export function isAircraftCacheAvailable(): boolean {
  return AIRCRAFT_CACHE_URL !== null;
}

// =============================================================================
// Events Cache (DynamoDB Cost Optimization)
// =============================================================================

/**
 * Events cache URL — served from CloudFront, updated every 5 minutes by ingestion Lambda.
 * Falls back to null if not configured (use API instead).
 */
const EVENTS_CACHE_URL: string | null = import.meta.env.VITE_EVENTS_URL || null;

/**
 * Cached events response from S3/CloudFront.
 */
export interface EventsCacheResponse {
  events: Array<{
    id: string;
    type: string;
    location: {
      lat: number;
      lon: number;
      radiusKm?: number;
    };
    region: string;
    observedAt: string;
    confidence: number | string;
    title: string;
    description: string;
    data: Record<string, unknown>;
  }>;
  metadata: {
    count: number;
    generatedAt: string;
  };
}

/**
 * Fetch cached events from CloudFront.
 *
 * This is the cost-optimized data source for fire events.
 * Data is refreshed every 5 minutes during ingestion.
 * Falls back to null if cache is not configured.
 *
 * Returns null if the events cache is not configured.
 */
export async function fetchEventsCached(): Promise<EventsCacheResponse | null> {
  if (!EVENTS_CACHE_URL) {
    return null;
  }

  const response = await fetch(EVENTS_CACHE_URL, {
    // Always get fresh from CloudFront edge (respects s-maxage)
    cache: 'no-cache',
  });

  if (!response.ok) {
    throw new ApiError(
      'Failed to fetch cached events',
      response.status
    );
  }

  return response.json();
}

/**
 * Check if the events cache is configured and available.
 */
export function isEventsCacheAvailable(): boolean {
  return EVENTS_CACHE_URL !== null;
}

/**
 * Search for social intelligence (news and social media links) for an incident.
 */
export async function fetchSocialIntel(params: {
  query: string;
  location?: string;
  region?: string;
}): Promise<SocialIntelResponse> {
  const queryParams = new URLSearchParams({
    query: params.query,
  });

  if (params.location) {
    queryParams.set('location', params.location);
  }

  if (params.region) {
    queryParams.set('region', params.region);
  }

  const response = await fetch(`${API_BASE_URL}/intel/search?${queryParams}`);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch social intel',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Photo response from the API.
 */
export interface PhotoResponse {
  id: string;
  url: string;
  fileName: string;
  lat: number;
  lon: number;
  altitude: number | null;
  takenAt: string | null;
  addedAt: string;
  status?: 'pending' | 'approved' | 'rejected';
}

/**
 * Response from the pre-signed URL endpoint.
 */
interface UploadUrlResponse {
  id: string;
  uploadUrl: string;
  expiresIn: number;
}

/**
 * Upload a photo using S3 pre-signed URL (production flow).
 * 1. Get a pre-signed URL from the API
 * 2. Upload directly to S3
 * 3. Confirm the upload with the API
 */
async function uploadPhotoViaS3(params: {
  file: File;
  lat: number;
  lon: number;
  altitude?: number;
  takenAt?: string;
  authToken?: string;
}): Promise<PhotoResponse> {
  // Build headers with auth token if provided
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (params.authToken) {
    headers['Authorization'] = `Bearer ${params.authToken}`;
  }

  // Step 1: Get pre-signed upload URL
  const urlResponse = await fetch(`${API_BASE_URL}/photos/upload-url`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      fileName: params.file.name,
      contentType: params.file.type || 'image/jpeg',
      lat: params.lat,
      lon: params.lon,
      altitude: params.altitude,
      takenAt: params.takenAt,
    }),
  });

  if (!urlResponse.ok) {
    const errorBody = await urlResponse.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to get upload URL',
      urlResponse.status,
      errorBody.details
    );
  }

  const { id, uploadUrl } = (await urlResponse.json()) as UploadUrlResponse;

  // Step 2: Upload directly to S3 using pre-signed URL
  const s3Response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': params.file.type || 'image/jpeg',
    },
    body: params.file,
  });

  if (!s3Response.ok) {
    throw new ApiError(
      'Failed to upload file to S3',
      s3Response.status,
      { s3Status: s3Response.statusText }
    );
  }

  // Step 3: Confirm upload with API
  const confirmResponse = await fetch(`${API_BASE_URL}/photos/confirm-upload/${id}`, {
    method: 'POST',
  });

  if (!confirmResponse.ok) {
    const errorBody = await confirmResponse.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to confirm upload',
      confirmResponse.status,
      errorBody.details
    );
  }

  return confirmResponse.json();
}

/**
 * Upload a photo using direct multipart form upload (local dev flow).
 */
async function uploadPhotoDirect(params: {
  file: File;
  lat: number;
  lon: number;
  altitude?: number;
  takenAt?: string;
  authToken?: string;
}): Promise<PhotoResponse> {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('lat', params.lat.toString());
  formData.append('lon', params.lon.toString());
  if (params.altitude !== undefined) {
    formData.append('altitude', params.altitude.toString());
  }
  if (params.takenAt) {
    formData.append('takenAt', params.takenAt);
  }

  // Build headers with auth token if provided
  const headers: Record<string, string> = {};
  if (params.authToken) {
    headers['Authorization'] = `Bearer ${params.authToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/photos/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to upload photo',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Upload a geotagged photo to the server.
 *
 * Requires authentication - pass an auth token from Cognito.
 *
 * Automatically selects the best upload method:
 * - Production (Lambda): Uses S3 pre-signed URL for direct upload
 * - Local development: Uses multipart form upload to backend
 */
export async function uploadPhoto(params: {
  file: File;
  lat: number;
  lon: number;
  altitude?: number;
  takenAt?: string;
  authToken?: string;
}): Promise<PhotoResponse> {
  // Try S3 flow first (production)
  try {
    return await uploadPhotoViaS3(params);
  } catch (error) {
    // If S3 flow returns 501 (Not Implemented), fall back to direct upload
    if (error instanceof ApiError && error.statusCode === 501) {
      if (import.meta.env.DEV) {
        console.log('[API Client] S3 upload not available, using direct upload');
      }
      return await uploadPhotoDirect(params);
    }
    throw error;
  }
}

/**
 * Paginated photos response from the API.
 */
export interface PaginatedPhotosResponse {
  photos: PhotoResponse[];
  total: number;
  skip: number;
  limit: number;
}

/**
 * Get all uploaded photos with pagination support.
 * For backwards compatibility, returns just the photos array by default.
 */
export async function fetchPhotos(options?: { skip?: number; limit?: number }): Promise<PhotoResponse[]> {
  const params = new URLSearchParams();
  if (options?.skip) params.set('skip', options.skip.toString());
  if (options?.limit) params.set('limit', options.limit.toString());

  const url = params.toString()
    ? `${API_BASE_URL}/photos?${params}`
    : `${API_BASE_URL}/photos`;

  const response = await fetch(url);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch photos',
      response.status,
      errorBody.details
    );
  }

  const data: PaginatedPhotosResponse = await response.json();
  return data.photos;
}

/**
 * Delete a photo by ID.
 *
 * Requires authentication - pass an auth token from Cognito.
 */
export async function deletePhoto(photoId: string, authToken?: string): Promise<void> {
  // Build headers with auth token if provided
  const headers: Record<string, string> = {};
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/photos/${photoId}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to delete photo',
      response.status,
      errorBody.details
    );
  }
}

// ============================================================================
// ADMIN API FUNCTIONS
// ============================================================================

/**
 * Get all pending photos awaiting moderation.
 * Requires admin authentication.
 */
export async function fetchPendingPhotos(authToken: string): Promise<PhotoResponse[]> {
  const response = await fetch(`${API_BASE_URL}/admin/photos/pending`, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch pending photos',
      response.status,
      errorBody.details
    );
  }

  const data: PaginatedPhotosResponse = await response.json();
  return data.photos;
}

/**
 * Get all photos with all statuses (admin view).
 * Requires admin authentication.
 */
export async function fetchAllPhotosAdmin(authToken: string): Promise<PhotoResponse[]> {
  const response = await fetch(`${API_BASE_URL}/admin/photos/all`, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch all photos',
      response.status,
      errorBody.details
    );
  }

  const data: PaginatedPhotosResponse = await response.json();
  return data.photos;
}

/**
 * Moderate a photo (approve or reject).
 * Requires admin authentication.
 */
export async function moderatePhoto(
  photoId: string,
  status: 'approved' | 'rejected',
  authToken: string
): Promise<{ status: string; id: string; newStatus: string }> {
  const response = await fetch(`${API_BASE_URL}/admin/photos/${photoId}/moderate`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to moderate photo',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Approve all pending photos at once.
 * Requires admin authentication.
 */
export async function approveAllPendingPhotos(authToken: string): Promise<{ approvedCount: number }> {
  const response = await fetch(`${API_BASE_URL}/admin/photos/approve-all`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to approve all photos',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

// ============================================================================
// PHOTO REMOVAL REQUEST API FUNCTIONS
// ============================================================================

/**
 * A removal request for a photo.
 */
export interface RemovalRequest {
  id: string;
  photoId: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  photo?: PhotoResponse;
}

/**
 * Request removal of a photo.
 * No authentication required - anyone can request removal.
 */
export async function requestPhotoRemoval(
  photoId: string,
  reason: string
): Promise<{ status: string; id: string; message: string }> {
  const response = await fetch(`${API_BASE_URL}/photos/${photoId}/request-removal`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ photoId, reason }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to submit removal request',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Get all removal requests (admin only).
 * Requires admin authentication.
 */
export async function fetchRemovalRequests(
  authToken: string,
  statusFilter?: 'pending' | 'approved' | 'rejected'
): Promise<RemovalRequest[]> {
  const url = new URL(`${API_BASE_URL}/admin/removal-requests`);
  if (statusFilter) {
    url.searchParams.set('status_filter', statusFilter);
  }

  const response = await fetch(url.toString(), {
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch removal requests',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Process a removal request (approve or reject).
 * Requires admin authentication.
 */
export async function processRemovalRequest(
  requestId: string,
  action: 'approve' | 'reject',
  authToken: string
): Promise<{ status: string; id: string; action: string; photoDeleted: boolean }> {
  const response = await fetch(`${API_BASE_URL}/admin/removal-requests/${requestId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to process removal request',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

// =============================================================================
// Comments API
// =============================================================================

export interface Comment {
  id: string;
  eventId: string;
  userId: string;
  userEmail: string;
  userName: string;
  content: string;
  status: 'active' | 'deleted';
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface CommentsListResponse {
  comments: Comment[];
  total: number;
}

/**
 * Fetch comments for a specific fire event
 */
export async function fetchComments(eventId: string): Promise<CommentsListResponse> {
  const response = await fetch(`${API_BASE_URL}/comments?event_id=${encodeURIComponent(eventId)}`);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch comments',
      response.status
    );
  }

  return response.json();
}

/**
 * Create a new comment on a fire event (requires authentication)
 */
export async function createComment(
  eventId: string,
  content: string,
  authToken: string
): Promise<Comment> {
  const response = await fetch(`${API_BASE_URL}/comments`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ eventId, content }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to create comment',
      response.status
    );
  }

  return response.json();
}

/**
 * Fetch all comments (admin only)
 */
export async function fetchAllCommentsAdmin(
  authToken: string,
  params?: {
    status?: 'active' | 'deleted';
    eventId?: string;
    limit?: number;
  }
): Promise<CommentsListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.set('status', params.status);
  if (params?.eventId) queryParams.set('event_id', params.eventId);
  if (params?.limit) queryParams.set('limit', params.limit.toString());

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/admin/comments${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch comments',
      response.status
    );
  }

  return response.json();
}

/**
 * Delete a comment (admin only - soft delete)
 */
export async function deleteCommentAdmin(
  commentId: string,
  authToken: string
): Promise<Comment> {
  const response = await fetch(`${API_BASE_URL}/admin/comments/${encodeURIComponent(commentId)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to delete comment',
      response.status
    );
  }

  return response.json();
}

// =============================================================================
// Watch Zones API
// =============================================================================

/**
 * Watch zone geometry - either circle or polygon.
 */
export interface WatchZoneGeometry {
  type: 'circle' | 'polygon';
  center?: { lat: number; lon: number };
  radiusKm?: number;
  coordinates?: Array<{ lat: number; lon: number }>;
}

/**
 * Notification preferences for a watch zone.
 */
export interface WatchZonePreferences {
  incidentTypes: string[];
  minSeverity: 'ALL' | 'WATCH_AND_ACT' | 'EMERGENCY';
  channels: {
    inApp: boolean;
    browserPush: boolean;
    email: boolean;
    emailFrequency: 'IMMEDIATE' | 'DAILY_DIGEST';
  };
  quietHours: {
    enabled: boolean;
    start: string;
    end: string;
    timezone: string;
    exceptEmergency: boolean;
  };
}

/**
 * A user's watch zone.
 */
export interface WatchZone {
  userId: string;
  zoneId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  isEnabled: boolean;
  geometry: WatchZoneGeometry;
  preferences: WatchZonePreferences;
}

/**
 * Input for creating a new watch zone.
 */
export interface CreateWatchZoneInput {
  name: string;
  geometry: WatchZoneGeometry;
  preferences?: Partial<WatchZonePreferences>;
}

/**
 * Input for updating a watch zone.
 */
export interface UpdateWatchZoneInput {
  name?: string;
  geometry?: WatchZoneGeometry;
  preferences?: Partial<WatchZonePreferences>;
  isEnabled?: boolean;
}

/**
 * Get all watch zones for the authenticated user.
 */
export async function fetchWatchZones(authToken: string): Promise<WatchZone[]> {
  const response = await fetch(`${API_BASE_URL}/watch-zones`, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch watch zones',
      response.status,
      errorBody.details
    );
  }

  const data = await response.json();
  // Transform geometry from API format for each zone
  return data.zones.map((zone: Record<string, unknown>) => ({
    ...zone,
    geometry: transformGeometryFromApi(zone.geometry as Record<string, unknown>),
  }));
}

/**
 * Get a specific watch zone.
 */
export async function fetchWatchZone(zoneId: string, authToken: string): Promise<WatchZone> {
  const response = await fetch(`${API_BASE_URL}/watch-zones/${zoneId}`, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch watch zone',
      response.status,
      errorBody.details
    );
  }

  // Transform geometry from API format
  const data = await response.json();
  return {
    ...data,
    geometry: transformGeometryFromApi(data.geometry),
  };
}

/**
 * Transform geometry to API format.
 * Converts coordinates from {lat, lon} objects to [lat, lon] arrays.
 */
function transformGeometryForApi(geometry: WatchZoneGeometry): Record<string, unknown> {
  const result: Record<string, unknown> = {
    type: geometry.type,
  };

  if (geometry.center) {
    result.center = geometry.center;
  }

  if (geometry.radiusKm !== undefined) {
    result.radiusKm = geometry.radiusKm;
  }

  if (geometry.coordinates && geometry.coordinates.length > 0) {
    // Convert from [{lat, lon}, ...] to [[lat, lon], ...]
    result.coordinates = geometry.coordinates.map(coord => [coord.lat, coord.lon]);
  }

  return result;
}

/**
 * Transform geometry from API format back to frontend format.
 * Converts coordinates from [lat, lon] arrays to {lat, lon} objects.
 */
function transformGeometryFromApi(geometry: Record<string, unknown>): WatchZoneGeometry {
  const result: WatchZoneGeometry = {
    type: geometry.type as 'circle' | 'polygon',
  };

  if (geometry.center) {
    result.center = geometry.center as { lat: number; lon: number };
  }

  if (geometry.radiusKm !== undefined) {
    result.radiusKm = geometry.radiusKm as number;
  }

  if (geometry.coordinates && Array.isArray(geometry.coordinates)) {
    // Convert from [[lat, lon], ...] to [{lat, lon}, ...]
    result.coordinates = (geometry.coordinates as number[][]).map(coord => ({
      lat: coord[0],
      lon: coord[1],
    }));
  }

  return result;
}

/**
 * Create a new watch zone.
 */
export async function createWatchZone(
  input: CreateWatchZoneInput,
  authToken: string
): Promise<WatchZone> {
  // Transform geometry to API format
  const apiInput = {
    ...input,
    geometry: transformGeometryForApi(input.geometry),
  };

  const response = await fetch(`${API_BASE_URL}/watch-zones`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(apiInput),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      formatErrorDetail(errorBody.detail) || 'Failed to create watch zone',
      response.status,
      errorBody.details
    );
  }

  // Transform geometry back from API format
  const data = await response.json();
  return {
    ...data,
    geometry: transformGeometryFromApi(data.geometry),
  };
}

/**
 * Update a watch zone.
 */
export async function updateWatchZone(
  zoneId: string,
  input: UpdateWatchZoneInput,
  authToken: string
): Promise<WatchZone> {
  // Transform geometry to API format if provided
  const apiInput = input.geometry
    ? { ...input, geometry: transformGeometryForApi(input.geometry) }
    : input;

  const response = await fetch(`${API_BASE_URL}/watch-zones/${zoneId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(apiInput),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      formatErrorDetail(errorBody.detail) || 'Failed to update watch zone',
      response.status,
      errorBody.details
    );
  }

  // Transform geometry back from API format
  const data = await response.json();
  return {
    ...data,
    geometry: transformGeometryFromApi(data.geometry),
  };
}

/**
 * Delete a watch zone.
 */
export async function deleteWatchZone(zoneId: string, authToken: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/watch-zones/${zoneId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to delete watch zone',
      response.status,
      errorBody.details
    );
  }
}

/**
 * Toggle a watch zone's enabled status.
 */
export async function toggleWatchZone(zoneId: string, authToken: string): Promise<WatchZone> {
  const response = await fetch(`${API_BASE_URL}/watch-zones/${zoneId}/toggle`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to toggle watch zone',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

// =============================================================================
// Notifications API
// =============================================================================

/**
 * A notification for a watch zone alert.
 */
export interface WatchZoneNotification {
  recipientId: string;
  notificationId: string;
  createdAt: string;
  zoneId: string;
  zoneName: string;
  eventId: string;
  eventType: string;
  alertType: 'NEW_INCIDENT' | 'ESCALATION' | 'UPDATE' | 'RESOLVED';
  title: string;
  body: string;
  severity: string;
  previousSeverity?: string;
  distanceKm: number;
  status: 'UNREAD' | 'READ' | 'DISMISSED';
  readAt?: string;
  channels: string[];
}

/**
 * Paginated notifications response.
 */
export interface NotificationsResponse {
  notifications: WatchZoneNotification[];
  total: number;
  skip: number;
  limit: number;
}

/**
 * Get notifications for the authenticated user.
 */
export async function fetchNotifications(
  authToken: string,
  options?: { skip?: number; limit?: number; status?: string }
): Promise<NotificationsResponse> {
  const params = new URLSearchParams();
  if (options?.skip) params.set('skip', options.skip.toString());
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.status) params.set('status', options.status);

  const queryString = params.toString();
  const url = `${API_BASE_URL}/notifications${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch notifications',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Get unread notification count.
 */
export async function fetchUnreadNotificationCount(authToken: string): Promise<number> {
  const response = await fetch(`${API_BASE_URL}/notifications/unread-count`, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch unread count',
      response.status,
      errorBody.details
    );
  }

  const data = await response.json();
  return data.count;
}

/**
 * Mark a notification as read.
 */
export async function markNotificationRead(
  notificationId: string,
  authToken: string
): Promise<WatchZoneNotification> {
  const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to mark notification as read',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Mark all notifications as read.
 */
export async function markAllNotificationsRead(authToken: string): Promise<{ updatedCount: number }> {
  const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to mark all as read',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Dismiss (delete) a notification.
 */
export async function dismissNotification(notificationId: string, authToken: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to dismiss notification',
      response.status,
      errorBody.details
    );
  }
}

// =============================================================================
// Push Subscriptions API
// =============================================================================

/**
 * Register a push subscription for notifications.
 */
export async function subscribeToPush(
  subscription: PushSubscriptionJSON,
  authToken: string
): Promise<{ status: string }> {
  const response = await fetch(`${API_BASE_URL}/push/subscribe`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(subscription),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to subscribe to push',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush(endpoint: string, authToken: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/push/unsubscribe`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ endpoint }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to unsubscribe from push',
      response.status,
      errorBody.details
    );
  }
}

// =============================================================================
// FCM Token Registration (Android App)
// =============================================================================

/**
 * Register an FCM token for native Android push notifications.
 * This is used by the Android TWA app to receive native notifications.
 */
export async function registerFcmToken(
  token: string,
  deviceId: string | undefined,
  authToken: string
): Promise<{ status: string; message: string }> {
  const response = await fetch(`${API_BASE_URL}/fcm/register`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token, deviceId }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to register FCM token',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Unregister an FCM token.
 */
export async function unregisterFcmToken(token: string, authToken: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/fcm/unregister?token=${encodeURIComponent(token)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to unregister FCM token',
      response.status,
      errorBody.details
    );
  }
}

// =============================================================================
// Supporter Subscriptions API
// =============================================================================

export interface SupporterFeatures {
  highResLayers: boolean;
  fireSpreadAnalysis: boolean;
  satelliteImagery: boolean;
}

export interface SubscriptionStatus {
  tier: 'free' | 'supporter' | 'trialing';
  status: 'none' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired';
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  features: SupporterFeatures;
}

export interface CheckoutSession {
  sessionId: string;
  url: string;
}

export interface PortalSession {
  url: string;
}

export interface StripeConfig {
  publishableKey: string;
}

export interface AdminUser {
  userId: string;
  email: string;
  emailVerified?: boolean;
  tier: string;
  status: string;
  enabled?: boolean;
  createdAt: string;
  lastModified?: string;
  currentPeriodEnd?: string | null;
  trialEndsAt?: string | null;
  cancelAtPeriodEnd?: boolean;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  stats: {
    total: number;
    active?: number;
    trialing: number;
    canceled?: number;
    supporters: number;
    free: number;
    verified?: number;
  };
  hasMore: boolean;
}

/**
 * Get Stripe configuration for frontend (publishable key).
 */
export async function fetchStripeConfig(): Promise<StripeConfig> {
  const response = await fetch(`${API_BASE_URL}/subscriptions/config`);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch Stripe config',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Get the current user's subscription status.
 */
export async function fetchSubscriptionStatus(authToken: string): Promise<SubscriptionStatus> {
  const response = await fetch(`${API_BASE_URL}/subscriptions/status`, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch subscription status',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Create a Stripe checkout session to start a subscription.
 */
export async function createCheckoutSession(
  params: { successUrl: string; cancelUrl: string },
  authToken: string
): Promise<CheckoutSession> {
  const response = await fetch(`${API_BASE_URL}/subscriptions/checkout`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to create checkout session',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Create a Stripe customer portal session for subscription management.
 */
export async function createPortalSession(
  params: { returnUrl: string },
  authToken: string
): Promise<PortalSession> {
  const response = await fetch(`${API_BASE_URL}/subscriptions/portal`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to create portal session',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Fetch all users from Cognito (admin only).
 * This shows all registered users regardless of subscription status.
 */
export async function fetchAdminUsers(
  authToken: string,
  params?: { tier?: string; status?: string; search?: string; limit?: number }
): Promise<AdminUsersResponse> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.set('limit', params.limit.toString());

  const url = queryParams.toString()
    ? `${API_BASE_URL}/admin/cognito-users?${queryParams}`
    : `${API_BASE_URL}/admin/cognito-users`;

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to fetch users',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}

/**
 * Update a user's supporter tier (admin only).
 * Allows admins to manually grant or revoke supporter status.
 */
export async function updateUserTier(
  userId: string,
  tier: 'supporter' | 'free',
  authToken: string
): Promise<{ success: boolean; userId: string; email: string; tier: string; message: string }> {
  const response = await fetch(
    `${API_BASE_URL}/admin/users/${encodeURIComponent(userId)}/tier?tier=${tier}`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody.detail || 'Failed to update user tier',
      response.status,
      errorBody.details
    );
  }

  return response.json();
}
